import { apiClient } from '@lib/api/client';
import { mockInvoiceHandlers } from '@lib/mocks/handlers';
import { MESSAGES } from '@lib/constants/messages';
import type { Invoice, InvoiceListResponse, EmailAttachments, EmailReceiver } from '../types/invoice.types';

// Usar mock handlers
const USE_MOCKS = false;

export const invoiceService = {
  async getInvoices(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    initDate?: string;
    endDate?: string;
    storeCode?: string;
    billingPoint?: string;
    cufe?: string;
    customerName?: string;
    documentNumber?: string;
    customerNumberId?: string;
    invoiceStatus?: string;
    documentType?: string;
    companyId?: string;
    cursor?: string;
  }, options?: { signal?: AbortSignal }): Promise<InvoiceListResponse> {
    console.log('📡 invoiceService.getInvoices called with params:', params);

    if (USE_MOCKS) {
      return mockInvoiceHandlers.getInvoices(params);
    }

    const response = await apiClient.get<InvoiceListResponse>('/invoices', {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
        status: params?.status,
        initDate: params?.initDate,
        endDate: params?.endDate,
        storeCode: params?.storeCode,
        billingPoint: params?.billingPoint,
        cufe: params?.cufe,
        customerName: params?.customerName,
        documentNumber: params?.documentNumber,
        customerNumberId: params?.customerNumberId,
        invoiceStatus: params?.invoiceStatus,
        documentType: params?.documentType,
        companyId: params?.companyId,
        // Cursor de escaneo incremental (busquedas con filtro de texto/nombre/
        // estado) — ver invoiceSearchCursor.ts en el backend.
        cursor: params?.cursor,
      },
      // Permite que React Query (via useInvoices) cancele la peticion HTTP
      // real cuando el usuario dispara una busqueda nueva antes de que esta
      // termine, en vez de dejarla completar en segundo plano sin que nadie
      // vaya a leer la respuesta.
      signal: options?.signal,
    });

    console.log('📡 invoiceService.getInvoices response:', {
      count: response.data.invoices?.length,
      total: response.data.total,
      firstInvoice: response.data.invoices?.[0]?.customerName
    });

    return response.data;
  },

  async getInvoicesArchive(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    initDate?: string;
    endDate?: string;
    storeCode?: string;
    billingPoint?: string;
    electronicInvoiceId?: string;
    cufe?: string;
    customerName?: string;
    documentNumber?: string;
    customerNumberId?: string;
    invoiceStatus?: string;
    documentType?: string;
    companyId?: string;
    cursor?: string;
  }, options?: { signal?: AbortSignal }): Promise<InvoiceListResponse> {
    if (USE_MOCKS) {
      return mockInvoiceHandlers.getInvoices(params);
    }

    const response = await apiClient.get<InvoiceListResponse>('/invoices/archive', {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
        status: params?.status,
        initDate: params?.initDate,
        endDate: params?.endDate,
        storeCode: params?.storeCode,
        billingPoint: params?.billingPoint,
        electronicInvoiceId: params?.electronicInvoiceId,
        cufe: params?.cufe,
        customerName: params?.customerName,
        documentNumber: params?.documentNumber,
        customerNumberId: params?.customerNumberId,
        invoiceStatus: params?.invoiceStatus,
        documentType: params?.documentType,
        companyId: params?.companyId,
        cursor: params?.cursor,
      },
      // Mismo motivo que en getInvoices: cancela la peticion HTTP real cuando
      // React Query descarta esta query por una busqueda mas nueva.
      signal: options?.signal,
    });

    return response.data;
  },

  async getInvoiceById(id: string): Promise<Invoice> {
    if (USE_MOCKS) {
      return mockInvoiceHandlers.getInvoiceById(id);
    }

    const response = await apiClient.get<Invoice>(`/invoices/${id}`);
    return response.data;
  },

  async generateCAFE(
    invoiceId: string,
    companyId: string,
    options?: {
      templateId?: string;
      sendEmail?: boolean;
    }
  ): Promise<{
    cafeUrl: string | null;
    message: string;
    success: boolean;
  }> {
    try {
      const response = await apiClient.post<{
        Data: string[];
        Status: {
          Code: string;
          Message: string;
        };
        Info: {
          Datetime: string;
          AcceptedUser: boolean;
        };
        Errors: any;
      }>(`/invoices/${invoiceId}/generate-cafe`, {
        companyId,
        templateId: options?.templateId || 'default',
        sendEmail: options?.sendEmail !== false,
      });

      const cafeUrl = response.data.Data && response.data.Data.length > 0 ? response.data.Data[0] : null;

      return {
        cafeUrl,
        message: response.data.Status.Message || 'CAFE generado exitosamente',
        success: response.data.Status.Code === '200',
      };
    } catch (error: any) {
      console.error('Error generating CAFE:', error);
      throw new Error(error?.response?.data?.message || 'Error al generar CAFE');
    }
  },

  async createInvoice(invoicePayload: any, idempotencyKey?: string): Promise<{
    status: 'success' | 'pending' | 'error';
    responseCode: string;
    message: string;
    data: any;
  }> {
    if (USE_MOCKS) {
      // Mock response for testing
      return {
        status: 'success',
        responseCode: '0920',
        message: 'Invoice processed successfully',
        data: {}
      };
    }

    console.log('📡 invoiceService.createInvoice called with payload:', invoicePayload);

    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['x-idempotency-key'] = idempotencyKey;
    }

    const response = await apiClient.post<{
      status: 'success' | 'pending' | 'error';
      responseCode: string;
      message: string;
      data: any;
    }>('/invoices', invoicePayload, { headers });

    console.log('📡 invoiceService.createInvoice response:', response.data);

    return response.data;
  },

  async updateInvoice(id: string, invoice: Partial<Invoice>): Promise<Invoice> {
    if (USE_MOCKS) {
      return mockInvoiceHandlers.updateInvoice(id, invoice);
    }
    throw new Error(MESSAGES.BACKEND_NOT_IMPLEMENTED);
  },

  async deleteInvoice(id: string): Promise<void> {
    if (USE_MOCKS) {
      await mockInvoiceHandlers.deleteInvoice(id);
      return;
    }
    throw new Error(MESSAGES.BACKEND_NOT_IMPLEMENTED);
  },

  async resendInvoiceEmail(
    invoiceId: string,
    companyId: string,
    attachments: EmailAttachments,
    receivers: EmailReceiver[]
  ): Promise<{
    message: string;
    success: boolean;
  }> {
    try {
      const response = await apiClient.post<{
        Data?: any;
        Status: {
          Code: string;
          Message: string;
        };
        Info: {
          Datetime: string;
          AcceptedUser: boolean;
        };
        Errors: any;
      }>(`/invoices/${invoiceId}/resend-email`, {
        companyId,
        attachments,
        receivers,
      });

      console.log('✅ Respuesta de reenvío de email:', response.data);

      // Verificar si fue exitoso (código 200)
      if (response.data.Status.Code === '200') {
        return {
          message: response.data.Status.Message || 'Email enviado exitosamente',
          success: true,
        };
      } else {
        // Si el código no es 200, es un error
        return {
          message: response.data.Status.Message || 'Error al enviar email',
          success: false,
        };
      }
    } catch (error: any) {
      console.error('❌ Error resending invoice email:', error);

      // Manejar errores del servidor
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al reenviar email';

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  async resendToDGI(
    invoiceId: string
  ): Promise<{
    responseCode: string | null;
    responseMessage: string | null;
    success: boolean;
    pending?: boolean;
  }> {
    try {
      const response = await apiClient.post<{
        status?: string;
        message?: string;
        responseCode: string | null;
        responseMessage: string | null;
        data: any;
      }>(`/invoices/${invoiceId}/resend-dgi`);

      if (response.data.status === 'pending') {
        return {
          responseCode: null,
          responseMessage: response.data.message || null,
          success: true,
          pending: true,
        };
      }

      return {
        responseCode: response.data.responseCode,
        responseMessage: response.data.responseMessage,
        success: true,
      };
    } catch (error: any) {
      console.error('Error resending invoice to DGI:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al reenviar factura a la DGI';
      return {
        responseCode: null,
        responseMessage: errorMessage,
        success: false,
      };
    }
  },

  async cancelInvoice(
    invoiceId: string,
    companyId: string,
    companyCode: string,
    reason?: string
  ): Promise<{
    message: string;
    success: boolean;
  }> {
    try {
      const response = await apiClient.post<{
        Data?: any;
        Status: {
          Code: string;
          Message: string;
        };
        Info: {
          Datetime: string;
          AcceptedUser: boolean;
        };
        Errors: any;
      }>(`/invoices/${invoiceId}/cancel`, {
        companyId,
        companyCode,
        reason: reason || 'Anulación de factura',
      });

      console.log('✅ Respuesta de anulación de factura:', response.data);

      // Verificar si fue exitoso (código 200)
      if (response.data.Status?.Code === '200') {
        return {
          message: response.data.Status.Message || 'Factura anulada exitosamente',
          success: true,
        };
      } else {
        // Si el código no es 200, es un error
        return {
          message: response.data.Status?.Message || 'Error al anular factura',
          success: false,
        };
      }
    } catch (error: any) {
      console.error('❌ Error al anular factura:', error);

      // Manejar errores del servidor
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al anular factura';

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  async getLocationCode(params: {
    province: string;
    district: string;
    township: string;
  }): Promise<{
    code: string;
    province: string;
    district: string;
    township: string;
  }> {
    try {
      console.log('📡 invoiceService.getLocationCode called with:', params);

      // Llamar al BFF que proxea a Aludra API
      // El BFF debe llamar a: POST https://qa-apim.aludra.cloud/mdl18-Portal/electronicInvoiceGetUbi
      const response = await apiClient.post<{
        Data: Array<{
          CODIGO_UBICACION: string;
          CODIGO_PROVINCIA: number;
          PROVINCIA: string;
          CODIGO_DISTRITO: number;
          DISTRITO: string;
          CODIGO_CORREGIMIENTO: number;
          CORREGIMIENTO: string;
        }>;
        Status: {
          Code: number;
          Message: string;
        };
        Info: {
          Datetime: string;
          AcceptedUser: boolean;
        };
      }>('/electronic-invoice/location', {
        province: params.province,
        district: params.district,
        township: params.township,
      });

      console.log('📡 invoiceService.getLocationCode response:', response.data);

      if (response.data.Data && response.data.Data.length > 0) {
        const location = response.data.Data[0];
        return {
          code: location.CODIGO_UBICACION,
          province: location.PROVINCIA,
          district: location.DISTRITO,
          township: location.CORREGIMIENTO,
        };
      }

      throw new Error('No se encontró el código de ubicación');
    } catch (error: any) {
      console.error('❌ Error al obtener código de ubicación:', error);

      // Mostrar más detalles del error
      if (error.response) {
        console.error('❌ Error response:', error.response.data);
        console.error('❌ Error status:', error.response.status);
      }

      throw error;
    }
  },
};
